import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Loader2, AlertCircle, Mail, Paperclip, Star, RefreshCw, GripVertical, MoreHorizontal, ChevronDown, ChevronUp, CheckCircle, Circle, Sparkles, Settings, Plus } from 'lucide-react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { mailApi, todoApi, subtasksApi, MailTodoLink } from '../../api/client';
import { formatDate } from '../../utils/date';
import { cn } from '../../utils/cn';
import { SubtaskList } from './SubtaskList';
import { FilterConfig } from '../../types/filter';

interface SmartKanbanColumnProps {
  column: {
    id: string;
    displayName: string;
    icon?: string;
    sourceId: string;
    filterConfig: FilterConfig;
    frozen?: boolean;
    hidden?: boolean;
    hideWhenEmpty?: boolean;
  };
  account: any;
  onSelectMail: (messageId: string) => void;
  selectedMessageId?: string;
  isFrozen?: boolean;
  isDragEnabled?: boolean;
  onQuickAction?: (mailData: { message: any; accountId: string; columnId: string }) => void;
  onAddTodo?: (mailData: { message: any; accountId: string }) => void;
  excludeMessageIds?: Set<string>;
  onMessagesLoaded?: (messageIds: string[]) => void;
  onEditColumn?: () => void;
  viewMode?: 'board' | 'list';
}

export default function SmartKanbanColumn({ 
  column, 
  account, 
  onSelectMail,
  selectedMessageId,
  isFrozen = false,
  isDragEnabled = true,
  onQuickAction,
  onAddTodo,
  excludeMessageIds,
  onMessagesLoaded,
  onEditColumn,
  viewMode = 'board',
}: SmartKanbanColumnProps) {
  const [loadedMessages, setLoadedMessages] = useState<any[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMoreToLoad, setHasMoreToLoad] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  
  // Initial fetch with smart filter
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['smartMessages', account?.id, column.id, column.filterConfig],
    queryFn: () => {
      if (!account) return { messages: [], hasMore: false, usedClientFilter: false };
      
      return mailApi.getSmartMessages(account.id, {
        filterConfig: column.filterConfig,
        baseSourceType: column.sourceId === 'all' ? 'all' : 'folder',
        baseSourceId: column.sourceId === 'all' ? undefined : column.sourceId,
        limit: 50,
        offset: 0,
      });
    },
    enabled: !!account && !!column.filterConfig,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
  
  // Update loaded messages when initial data changes
  useEffect(() => {
    if (data?.messages) {
      setLoadedMessages(data.messages as any[]);
      setHasMoreToLoad(data.hasMore);
      setOffset(data.messages.length);
    }
  }, [data]);
  
  // Load more messages
  const loadMore = useCallback(async () => {
    if (!account || isLoadingMore || !hasMoreToLoad) return;
    
    setIsLoadingMore(true);
    try {
      const result = await mailApi.getSmartMessages(account.id, {
        filterConfig: column.filterConfig,
        baseSourceType: column.sourceId === 'all' ? 'all' : 'folder',
        baseSourceId: column.sourceId === 'all' ? undefined : column.sourceId,
        limit: 50,
        offset,
      });
      setLoadedMessages(prev => [...prev, ...(result.messages as any[])]);
      setHasMoreToLoad(result.hasMore);
      setOffset(prev => prev + result.messages.length);
    } catch (err) {
      console.error('Failed to load more smart messages:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [account, column, offset, isLoadingMore, hasMoreToLoad]);
  
  // Scroll handler
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    
    if (scrollHeight - scrollTop - clientHeight < 100 && hasMoreToLoad && !isLoadingMore) {
      loadMore();
    }
  }, [loadMore, hasMoreToLoad, isLoadingMore]);
  
  const rawMessages = loadedMessages;
  const messageIds = useMemo(() => rawMessages.map((m: any) => m.id), [rawMessages]);
  
  // Fetch todo links
  const { data: todoLinksData } = useQuery({
    queryKey: ['mailTodoLinks', account?.id, messageIds],
    queryFn: () => {
      if (!account || messageIds.length === 0) return { links: {} };
      return todoApi.getMailLinksBatch(account.id, messageIds);
    },
    enabled: !!account && messageIds.length > 0,
    staleTime: 30 * 1000,
  });
  
  const todoLinks = todoLinksData?.links || {};
  
  // Filter out duplicates and sort by date (newest first)
  const messages = useMemo(() => {
    let filtered = rawMessages;
    
    if (excludeMessageIds && excludeMessageIds.size > 0) {
      filtered = filtered.filter((m: any) => !excludeMessageIds.has(m.id));
    }
    
    // Sort by receivedAt descending (newest first)
    filtered = [...filtered].sort((a: any, b: any) => {
      const dateA = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
      const dateB = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
      return dateB - dateA;
    });
    
    return filtered;
  }, [rawMessages, excludeMessageIds]);
  
  // Report loaded message IDs
  useEffect(() => {
    if (onMessagesLoaded && messageIds.length > 0) {
      onMessagesLoaded(messageIds);
    }
  }, [messageIds, onMessagesLoaded]);
  
  // Drop target
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `column-${column.id}`,
    data: {
      type: 'column',
      column,
      accountId: account?.id,
    },
  });
  
  // Hide if empty and configured
  if (column.hideWhenEmpty && !isLoading && messages.length === 0) {
    return null;
  }
  
  return (
    <div 
      ref={setDropRef}
      className={cn(
        cn('flex flex-col overflow-hidden transition-all', viewMode === 'list' ? 'flex-1 min-h-0' : 'flex-shrink-0 rounded-xl shadow-theme'),
        viewMode === 'list' ? 'w-full' : 'w-80',
        isOver && 'ring-2 ring-primary ring-offset-2'
      )}
      style={{
        backgroundColor: isOver ? 'var(--bg-hover)' : 'var(--bg-primary)',
        border: `1px solid ${isOver ? 'var(--brand-primary)' : 'var(--border-default)'}`,
        maxHeight: viewMode === 'list' ? '100%' : 'calc(100vh - 180px)',
        minHeight: '200px',
        height: viewMode === 'list' ? '100%' : 'fit-content',
        ...(isFrozen ? {
          position: 'sticky',
          left: 0,
          zIndex: 10,
          boxShadow: '4px 0 12px rgba(0,0,0,0.15)',
        } : {}),
      }}
    >
      {/* Column Header */}
      <div 
        className="flex items-center justify-between px-4 py-3"
        style={{
          borderBottom: '1px solid var(--border-default)',
          background: isFrozen 
            ? 'var(--brand-primary)' 
            : 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">{column.icon || '🔍'}</span>
          <h3 
            className="font-semibold flex items-center gap-1.5"
            style={{ color: isFrozen ? 'white' : 'var(--text-primary)' }}
          >
            {column.displayName}
            <Sparkles className="w-3 h-3" style={{ color: isFrozen ? 'white' : 'var(--brand-primary)' }} />
          </h3>
          <span 
            className="text-xs px-2 py-0.5 rounded-full font-medium"
            style={{ 
              backgroundColor: isFrozen ? 'rgba(255,255,255,0.2)' : 'var(--bg-tertiary)',
              color: isFrozen ? 'white' : 'var(--text-secondary)',
            }}
          >
            {hasMoreToLoad ? `${messages.length}+` : messages.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {onEditColumn && (
            <button 
              onClick={onEditColumn}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: isFrozen ? 'white' : 'var(--text-tertiary)' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = isFrozen ? 'rgba(255,255,255,0.1)' : 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Spalte bearbeiten"
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
          <button 
            onClick={() => refetch()}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: isFrozen ? 'white' : 'var(--text-tertiary)' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = isFrozen ? 'rgba(255,255,255,0.1)' : 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Messages List */}
      <div 
        ref={listRef}
        className={cn(
          'flex-1 overflow-y-auto',
          viewMode === 'list' ? 'p-0' : 'p-2 space-y-2'
        )}
        onScroll={handleScroll}
      >
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-lg animate-pulse" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-default)', padding: '0.75rem' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="h-3.5 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '40%' }} />
                  <div className="h-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '15%' }} />
                </div>
                <div className="h-3.5 rounded mb-2" style={{ backgroundColor: 'var(--bg-tertiary)', width: '80%' }} />
                <div className="h-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '60%' }} />
                <div className="flex gap-2 mt-3">
                  <div className="h-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '20%' }} />
                  <div className="h-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', width: '15%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="w-5 h-5 text-error mb-2" />
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Fehler beim Laden</p>
            <button 
              onClick={() => refetch()}
              className="mt-2 text-xs text-primary hover:underline"
            >
              Erneut versuchen
            </button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Mail className="w-8 h-8 mb-2" style={{ color: 'var(--text-disabled)' }} />
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Keine E-Mails entsprechen den Filterregeln
            </p>
          </div>
        ) : (
          <>
            {messages.map((message: any) => (
              <SmartMailCard
                key={message.id}
                message={message}
                accountId={account?.id}
                columnId={column.id}
                isSelected={selectedMessageId === message.id}
                onClick={() => onSelectMail(message.id)}
                isDragEnabled={isDragEnabled}
                onQuickAction={onQuickAction}
                onAddTodo={onAddTodo}
                todoLink={todoLinks[message.id]}
                provider={account?.provider || 'microsoft'}
                viewMode={viewMode}
              />
            ))}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                <span className="ml-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>Lade mehr...</span>
              </div>
            )}
            {hasMoreToLoad && !isLoadingMore && (
              <button
                onClick={loadMore}
                className="w-full py-2 text-xs transition-colors rounded-lg"
                style={{ color: 'var(--text-tertiary)' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Mehr laden...
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Smart mail card - same design as DraggableMailCard in KanbanColumn
function SmartMailCard({ 
  message, 
  accountId, 
  columnId, 
  isSelected, 
  onClick,
  isDragEnabled,
  onQuickAction,
  onAddTodo,
  todoLink,
  provider,
  viewMode = 'board',
}: {
  message: any;
  accountId: string;
  columnId: string;
  isSelected: boolean;
  onClick: () => void;
  isDragEnabled: boolean;
  onQuickAction?: (mailData: { message: any; accountId: string; columnId: string }) => void;
  onAddTodo?: (mailData: { message: any; accountId: string }) => void;
  todoLink?: MailTodoLink;
  provider: string;
  viewMode?: 'board' | 'list';
}) {
  const [showTasks, setShowTasks] = useState(false);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  const queryClient = useQueryClient();
  
  // Fetch subtasks count to determine if we should auto-expand
  const { data: subtasksData } = useQuery({
    queryKey: ['subtasks', accountId, message.id],
    queryFn: () => subtasksApi.getSubtasks(accountId, message.id),
    enabled: provider === 'microsoft' && !!todoLink,
    staleTime: 10 * 1000,
  });
  
  const subtasksCount = subtasksData?.subtasks?.length || 0;
  
  // Auto-expand only when there are actual subtasks
  useEffect(() => {
    if (todoLink && !hasAutoExpanded && subtasksCount > 0) {
      setShowTasks(true);
      setHasAutoExpanded(true);
    }
  }, [todoLink, hasAutoExpanded, subtasksCount]);
  
  // Toggle todo completion mutation
  const toggleTodoMutation = useMutation({
    mutationFn: () => todoApi.toggleMailTodo(accountId, message.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mailTodoLink', accountId, message.id] });
      queryClient.invalidateQueries({ queryKey: ['mailTodoLinks'] });
    },
  });
  
  const handleToggleTodo = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleTodoMutation.mutate();
  };
  
  const handleAddTodo = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddTodo) {
      onAddTodo({ message, accountId });
    }
  };
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `mail-${message.id}`,
    data: {
      type: 'mail',
      message,
      accountId,
      columnId,
    },
    disabled: !isDragEnabled,
  });
  
  const style = transform ? {
    transform: CSS.Translate.toString(transform),
    zIndex: 1000,
    opacity: isDragging ? 0.8 : 1,
  } : undefined;
  
  // ===== LIST MODE: Compact Outlook/Gmail-style row =====
  if (viewMode === 'list') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'w-full text-left transition-colors cursor-pointer group',
          isSelected && 'ring-1 ring-primary',
          isDragging && 'opacity-60'
        )}
        onClick={onClick}
        {...attributes}
      >
        <div 
          className="flex items-start gap-1"
          style={{
            backgroundColor: isSelected ? 'var(--bg-hover)' : 'transparent',
            borderBottom: '1px solid var(--border-default)',
            borderLeft: message.importance === 'high' 
              ? '3px solid var(--color-error)' 
              : message.importance === 'low'
                ? '3px solid var(--text-disabled)'
                : '3px solid transparent',
            padding: '0.375rem 0.75rem',
          }}
          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          {isDragEnabled && (
            <div 
              {...listeners}
              className="hidden sm:flex flex-shrink-0 items-center pt-1 cursor-grab active:cursor-grabbing opacity-0 hover:opacity-100 transition-opacity"
              onClick={e => e.stopPropagation()}
            >
              <GripVertical className="w-3 h-3" style={{ color: 'var(--text-disabled)' }} />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            {/* Row 1: Sender ......... Date */}
            <div className="flex items-center justify-between gap-2">
              <span className={cn('text-sm truncate', !message.isRead && 'font-semibold')} style={{ color: 'var(--text-primary)' }}>
                {message.from.name || message.from.email}
              </span>
              <span className="text-xs whitespace-nowrap flex-shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                {formatDate(message.receivedAt)}
              </span>
            </div>
            
            {/* Row 2: Subject [📎] [⭐] [Badges] */}
            <div className="flex items-center gap-1.5">
              <span className={cn('text-sm truncate flex-1 min-w-0', !message.isRead && 'font-semibold')} style={{ color: 'var(--text-primary)' }}>
                {message.subject}
              </span>
              {message.hasAttachments && <Paperclip className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />}
              {message.importance === 'high' && <Star className="w-3 h-3 flex-shrink-0 text-warning fill-warning" />}
              {todoLink && (
                <button
                  onClick={handleToggleTodo}
                  disabled={toggleTodoMutation.isPending}
                  className="inline-flex items-center gap-0.5 text-[10px] px-1 py-0 rounded font-medium flex-shrink-0"
                  style={{
                    backgroundColor: todoLink.completed ? 'rgba(16, 185, 129, 0.15)' : 'rgba(234, 179, 8, 0.15)',
                    color: todoLink.completed ? 'var(--color-success)' : 'var(--color-warning)',
                  }}
                >
                  {todoLink.completed ? <CheckCircle className="w-2.5 h-2.5" /> : <Circle className="w-2.5 h-2.5" />}
                </button>
              )}
              {message.categories?.slice(0, 2).map((cat: string, i: number) => (
                <span key={i} className="text-[10px] px-1 py-0 rounded font-medium flex-shrink-0 truncate max-w-[80px]" style={{ backgroundColor: 'rgba(99, 102, 241, 0.15)', color: 'var(--brand-primary)' }}>
                  {cat}
                </span>
              ))}
            </div>
            
            {/* Row 3: Preview */}
            {message.bodyPreview && (
              <p className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                {message.bodyPreview}
              </p>
            )}
          </div>
          
          {onQuickAction && (
            <button
              className="flex-shrink-0 p-1 rounded transition-opacity opacity-0 group-hover:opacity-100"
              style={{ color: 'var(--text-tertiary)' }}
              onClick={e => { e.stopPropagation(); onQuickAction({ message, accountId, columnId }); }}
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ===== BOARD MODE: Original card layout =====
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'w-full text-left transition-all cursor-pointer p-3 rounded-lg',
        isSelected && 'ring-2 ring-primary',
        isDragging && 'shadow-2xl'
      )}
      onClick={onClick}
      {...attributes}
    >
      <div 
        className="flex items-start gap-2"
        style={{
          backgroundColor: isSelected 
            ? 'var(--bg-hover)' 
            : message.isRead 
              ? 'var(--bg-primary)' 
              : 'rgba(99, 102, 241, 0.08)',
          border: `1px solid ${isSelected ? 'var(--brand-primary)' : 'var(--border-default)'}`,
          borderLeft: message.importance === 'high' 
            ? '3px solid var(--color-error)' 
            : message.importance === 'low'
              ? '3px solid var(--text-disabled)'
              : '3px solid transparent',
          borderRadius: '0.5rem',
          padding: '0.75rem',
        }}
      >
        {isDragEnabled && (
          <div className="flex-shrink-0 flex flex-col gap-1 -ml-1">
            <div 
              {...listeners}
              className="hidden sm:block cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-black/5"
              onClick={e => e.stopPropagation()}
            >
              <GripVertical className="w-4 h-4" style={{ color: 'var(--text-disabled)' }} />
            </div>
            {onQuickAction && (
              <button
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: 'var(--text-tertiary)' }}
                onClick={e => {
                  e.stopPropagation();
                  onQuickAction({ message, accountId, columnId });
                }}
                onTouchStart={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                onTouchEnd={e => e.currentTarget.style.backgroundColor = 'transparent'}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                title="Verschieben..."
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <span 
              className={cn('text-sm truncate', !message.isRead && 'font-semibold')}
              style={{ color: 'var(--text-primary)' }}
            >
              {message.from.name || message.from.email}
            </span>
            <span className="text-xs whitespace-nowrap" style={{ color: 'var(--text-tertiary)' }}>
              {formatDate(message.receivedAt)}
            </span>
          </div>
          
          <p className={cn('text-sm truncate mb-1', !message.isRead && 'font-semibold')} style={{ color: 'var(--text-primary)' }}>
            {message.subject}
          </p>
          
          <p className="text-xs break-all" style={{ color: 'var(--text-tertiary)' }}>
            {message.bodyPreview}
          </p>
          
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {message.hasAttachments && <Paperclip className="w-3 h-3" style={{ color: 'var(--text-tertiary)' }} />}
            {message.importance === 'high' && <Star className="w-3 h-3 text-warning fill-warning" />}
            {todoLink ? (
              <button
                onClick={handleToggleTodo}
                disabled={toggleTodoMutation.isPending}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium transition-all hover:scale-105"
                style={{
                  backgroundColor: todoLink.completed ? 'rgba(16, 185, 129, 0.15)' : 'rgba(234, 179, 8, 0.15)',
                  color: todoLink.completed ? 'var(--color-success)' : 'var(--color-warning)',
                  cursor: toggleTodoMutation.isPending ? 'wait' : 'pointer',
                }}
              >
                {toggleTodoMutation.isPending ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : todoLink.completed ? <CheckCircle className="w-2.5 h-2.5" /> : <Circle className="w-2.5 h-2.5" />}
                {todoLink.completed ? '✓ Erledigt' : 'Offen'}
              </button>
            ) : onAddTodo && (
              <button
                onClick={handleAddTodo}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium transition-all hover:scale-105"
                style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)', color: 'var(--brand-primary)', border: '1px dashed var(--brand-primary)' }}
              >
                <Plus className="w-2.5 h-2.5" />
                Aufgabe
              </button>
            )}
            {message.categories?.slice(0, 2).map((cat: string, i: number) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: 'rgba(99, 102, 241, 0.15)', color: 'var(--brand-primary)' }}>
                {cat}
              </span>
            ))}
          </div>
          
          {todoLink && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowTasks(!showTasks); }}
              className="flex items-center gap-1 mt-2 text-xs transition-colors"
              style={{ color: 'var(--text-tertiary)' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--brand-primary)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
            >
              {showTasks ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              <span>{showTasks ? 'Subtasks ausblenden' : 'Subtasks anzeigen'}</span>
            </button>
          )}
        </div>
      </div>
      
      {showTasks && todoLink && (
        <div 
          className="mt-3 ml-1 pl-3 py-2 rounded-r-lg"
          style={{ borderLeft: '2px solid var(--brand-primary)', backgroundColor: 'var(--bg-secondary)' }}
          onClick={e => e.stopPropagation()}
        >
          <SubtaskList accountId={accountId} messageId={message.id} provider={provider} todoLinkExists={!!todoLink} />
        </div>
      )}
    </div>
  );
}
